import {
  getScrollBarWidth,
  getStickyScrollY,
  isMatchMedia,
  isMatchMediaByRoot,
  observeElementIntersection,
  setPropertyValue,
} from '%helpers%/utils';
import {selectDropdownItem} from '%helpers%/helpers';
import {debounce, throttle} from 'lodash';
import EventHandler from 'bootstrap/js/src/dom/event-handler';
import Swiper from 'swiper';

export const isHeaderCollapsed = () => isMatchMediaByRoot('--bs-breakpoint-header-collapsed');
export const isDesktop = () => isMatchMedia('desktop');

export const initiatePageLayout = () => {
  initiatePageHeader();
  initPageScrollSpy();
  initPageSidenav();
  initPageAlertbar();
};

function initiatePageHeader() {
  const header = document.querySelector('.page-header');
  if (!header) return;

  const body = document.body;
  const pageAlertbar = document.querySelector('.page-alertbar');
  const navbarHeader = header.querySelector('.navbar-header');
  const navbarCollapse = header.querySelector('.navbar-collapse');
  const navbarMain = header.querySelector('.navbar-main');
  const searchDropdown = header.querySelector('.search-dropdown-menu');

  const setRightShift = (value = 0, onlyNavbar) => {
    const targetEl = onlyNavbar ? navbarHeader : document.documentElement;
    if (!targetEl) return;

    if (value) {
      const currentShift = parseInt(getComputedStyle(targetEl).getPropertyValue('--scrollbar-offset')) || 0;
      if (currentShift === value) return;
      targetEl.style.setProperty('--scrollbar-offset', `${Math.round(value)}px`);
    } else targetEl.style.removeProperty('--scrollbar-offset');
  };

  const updateScrollbarOffset = (apply, onlyNavbar = false) => {
    const scrollbarWidth = getScrollBarWidth();
    const value = apply ? scrollbarWidth : 0;
    setRightShift(value, onlyNavbar);
  };

  const toggleSticky = () => {
    const isSticky = pageAlertbar
      ? window.scrollY > pageAlertbar.offsetHeight + header.offsetHeight
      : window.scrollY > header.offsetHeight;
    if (isSticky && !header.classList.contains('is-sticky')) {
      header.classList.add('is-sticky');
      body.classList.add('is-header-sticky');
    } else if (!isSticky && header.classList.contains('is-sticky')) {
      header.classList.remove('is-sticky');
      body.classList.remove('is-header-sticky');
    }
  };

  const handleClickOutside = e => {
    const collapseEl = header.querySelector('.collapse.show');
    if (
      collapseEl &&
      (!isHeaderCollapsed() || !collapseEl.closest('.navbar-container')) &&
      !collapseEl.contains(e.target) &&
      !e.target.closest('.modal')
    ) {
      bootstrap.Collapse.getOrCreateInstance(collapseEl).hide();
    }
  };

  const handleNavbarCollapse = ({type, target}) => {
    if (!target.classList.contains('navbar-collapse')) return;
    const shown = type === 'show.bs.collapse';
    updateScrollbarOffset(shown && isHeaderCollapsed());
    header.classList.toggle('is-open', shown);
    body.classList.toggle('header-navbar-open', shown);
  };

  document.querySelectorAll('.navbar-nav-main > .nav-item.dropdown').forEach(navItem => {
    var navLink = navItem.querySelector('a.nav-link.dropdown-toggle.d-none.d-lg-flex');
    var dropdownMenu = navItem.querySelector('.dropdown-menu');

    if (navLink && dropdownMenu) {
      navItem.addEventListener('mouseenter', function () {
        if (isDesktop()) {
          navItem.classList.add('show');
          dropdownMenu.classList.add('show');
          navLink.setAttribute('aria-expanded', 'true');
        }
      });

      navItem.addEventListener('mouseleave', function () {
        if (isDesktop()) {
          navItem.classList.remove('show');
          dropdownMenu.classList.remove('show');
          navLink.setAttribute('aria-expanded', 'false');
        }
      });
    }
  });

  const handleDropdown = ({type, target}) => {
    const shown = type.startsWith('show');

    const dropdown = target.closest('.dropdown');
    dropdown?.classList.toggle('open', shown);

    if (!isDesktop()) return;

    const stillOpen = navbarMain.querySelector('.dropdown-menu.show');
    const currentMenu = dropdown?.querySelector('.dropdown-menu');
    const width = shown ? getScrollBarWidth() : 0;

    if (shown) {
      if (stillOpen && currentMenu && stillOpen !== currentMenu) stillOpen.classList.remove('show');
      if (!body.classList.contains('header-dropdown-open')) {
        setRightShift(width);
        header.classList.add('is-header-dropdown-open');
        body.classList.add('header-dropdown-open');
      }
    } else {
      if (!stillOpen || stillOpen === currentMenu) {
        if (!body.classList.contains('modal-open') && !body.classList.contains('offcanvas-open')) {
          setRightShift(0);
        }
        header.classList.remove('is-header-dropdown-open');
        body.classList.remove('header-dropdown-open');
      }
    }
  };

  const changeDropdownMode = () => {
    navbarMain.querySelectorAll('.main-dropdown-menu').forEach(el => {
      const activeToggle = navbarMain.querySelector(
        `[data-bs-toggle="${isDesktop() ? 'collapse' : 'dropdown'}"][aria-expanded="true"]`
      );
      if (activeToggle) activeToggle.click();
      el.classList.toggle('dropdown-menu', isDesktop());
      el.classList.toggle('collapse', !isDesktop());
    });
  };

  const handleScroll = throttle(() => {
    toggleSticky();
  }, 100);

  const handleResize = debounce(() => {
    toggleSticky();
    changeDropdownMode();
    if (isDesktop() && body.classList.contains('header-navbar-open')) {
      bootstrap.Collapse.getOrCreateInstance(navbarCollapse).hide();
    } else if (!isDesktop() && body.classList.contains('header-dropdown-open')) {
      body.classList.remove('header-dropdown-open');
      setRightShift(0);
    }
  }, 150);

  changeDropdownMode();

  if (searchDropdown) {
    const searchInputWrapper = searchDropdown.querySelector('.search-input-wrapper');
    const searchPopularTags = searchDropdown.querySelector('.tags-block-wrapper');

    const handleFocus = ({type}) => {
      const isFocus = type === 'focus';
      header.classList.toggle('is-search-focus', isFocus);
      searchPopularTags?.classList.toggle('d-none', !isDesktop() && !isFocus);
    };

    EventHandler.on(searchInputWrapper, 'focus', '.ts-control input', handleFocus);
    EventHandler.on(searchInputWrapper, 'blur', '.ts-control input', handleFocus);
  }

  //== Events

  window.addEventListener('scroll', handleScroll, {passive: true});
  window.addEventListener('resize', handleResize, {passive: true});

  document.addEventListener('click', handleClickOutside);
  ['show.bs.collapse', 'hide.bs.collapse'].forEach(event =>
    navbarCollapse?.addEventListener(event, handleNavbarCollapse)
  );
  ['show.bs.dropdown', 'hide.bs.dropdown', 'show.bs.collapse', 'hide.bs.collapse'].forEach(event =>
    navbarMain?.addEventListener(event, handleDropdown)
  );
  ['show.bs.modal', 'hidden.bs.modal', 'show.bs.offcanvas', 'hidden.bs.offcanvas'].forEach(event =>
    document.addEventListener(event, () => updateScrollbarOffset(event.startsWith('show'), true))
  );
}

function initPageScrollSpy() {
  const navbarPageScrollspy = document.getElementById('navbarPageScrollspy');
  if (!navbarPageScrollspy) return;

  const pageHeader = document.querySelector('.page-header');
  const wrapper = navbarPageScrollspy.closest('.widget-wrapper');
  const dropdownSelect = navbarPageScrollspy.querySelector('.dropdown-select');
  const navLinks = navbarPageScrollspy.querySelectorAll('.nav-link');
  const dropdownItems = dropdownSelect?.querySelectorAll('.dropdown-item');

  const sectionIdsInMenu = Array.from(navLinks)
    .map(link => link.getAttribute('href')?.slice(1))
    .filter(Boolean);
  const sectionsInMenu = sectionIdsInMenu.map(id => document.getElementById(id)).filter(Boolean);

  let visibleSections = new Set();
  let lastVisibleSectionIndex = -1;

  const getLastVisibleSectionBottom = () => {
    const lastSection = sectionsInMenu[sectionsInMenu.length - 1];
    return lastSection?.getBoundingClientRect().bottom + window.scrollY;
  };

  sectionsInMenu.forEach((section, index) => {
    const id = section.id;

    observeElementIntersection(
      section,
      isIntersecting => {
        const rect = section.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
        const ratio = visibleHeight / rect.height;

        const thresholdRatio = isMatchMedia('desktop') ? 0.5 : 0.6;

        if (isIntersecting && ratio >= thresholdRatio) {
          visibleSections.add(id);
          lastVisibleSectionIndex = index;
        } else {
          visibleSections.delete(id);
        }

        if (isIntersecting && ratio >= thresholdRatio) {
          navLinks.forEach(el => el.classList.remove('active'));
          navbarPageScrollspy
            .querySelectorAll(`.nav-link[href="#${id}"]`)
            .forEach(el => el.classList.add('active'));

          if (dropdownSelect) {
            dropdownItems.forEach(item => item.classList.remove('active'));
            const activeItem = dropdownSelect.querySelector(`[href="#${id}"]`);
            if (activeItem) selectDropdownItem(activeItem, false);
          }
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 0px',
        threshold: isMatchMedia('desktop') ? [0.5] : [0.6],
      }
    );
  });

  const checkNavbarHidden = () => {
    const scrollY = window.scrollY || window.pageYOffset;
    const lastSectionBottom = getLastVisibleSectionBottom();
    const shouldHide = visibleSections.size === 0 && scrollY > lastSectionBottom;

    wrapper.style.position = shouldHide ? 'relative' : 'sticky';
  };

  const isSticky = () => {
    const scrollY = window.scrollY || window.pageYOffset;
    const stickyStart = getStickyScrollY(wrapper) - 24;
    wrapper.classList.toggle('is-sticky', scrollY >= stickyStart);
  };

  const setScrollMarginTop = () => {
    const height = isMatchMedia('header-collapsed')
      ? pageHeader?.offsetHeight || 0
      : wrapper?.offsetHeight || 0;
    setPropertyValue('--bs-scroll-margin-top', 'calc(var(--bs-container-offset) + ' + height + 'px)');
  };

  setScrollMarginTop();

  let resizeTimeout;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setScrollMarginTop();
      }, 100);
    },
    {passive: true}
  );

  window.addEventListener(
    'scroll',
    () => {
      setScrollMarginTop();
      isSticky();
      checkNavbarHidden();
    },
    {passive: true}
  );
}

function initPageSidenav() {
  const sidenav = document.querySelector('.page-sidenav');
  if (!sidenav) return;

  const scrollTopBtn = sidenav.querySelector('[data-toggle="scrollTop"]');

  const handleScroll = () => {
    if (!scrollTopBtn) return;
    scrollTopBtn.classList[window.scrollY > 10 ? 'remove' : 'add']('invisible');
  };
  const handleResize = () => {
    handleScroll();
  };

  handleResize();

  window.addEventListener('scroll', handleScroll, {passive: true});
  window.addEventListener('resize', handleResize, {passive: true});
}

function initPageAlertbar() {
  const pageAlertbar = document.querySelector('.page-alertbar');
  if (!pageAlertbar) return;

  const swiperEl = pageAlertbar.querySelector('.swiper');
  if (!swiperEl) return;

  const btnNext = pageAlertbar.querySelector('.swiper-btn-next');
  const btnPrev = pageAlertbar.querySelector('.swiper-btn-prev');

  const swiper = new Swiper(swiperEl, {
    loop: true,
    slidesPerView: 1,
    speed: 600,
    navigation: {
      nextEl: btnNext,
      prevEl: btnPrev,
    },
    effect: 'fade',
    fadeEffect: {crossFade: true},
    on: {
      init(swiper) {},
    },
  });

  const toggleShown = ({type}) => {
    switch (type) {
      case 'shown.bs.collapse':
        swiper.update();
        break;
      case 'hide.bs.collapse':
        pageAlertbar.classList.add('fadeOut');
        break;
      case 'hidden.bs.collapse':
        pageAlertbar.classList.remove('fadeOut');
        break;
      default:
        break;
    }
  };

  pageAlertbar.addEventListener('shown.bs.collapse', toggleShown);
  pageAlertbar.addEventListener('hide.bs.collapse', toggleShown);
  pageAlertbar.addEventListener('hidden.bs.collapse', toggleShown);
}
